import { isEqual } from "lodash";
import Router, { useRouter } from "next/router";
import type { ParsedUrlQueryInput } from "querystring";
import { startTransition, useCallback, useMemo } from "react";
import type { ZodSchema } from "zod";
import { URL_FILTERS_SEARCH_KEY } from "~/utils/constants";

export function useUrlFilters<T extends Record<string, any>>(
  defaultFilters: T,
  validationSchema: ZodSchema,
) {
  const { query } = useRouter();

  const queryFilters = query[URL_FILTERS_SEARCH_KEY];

  const filters = useMemo(() => {
    let _filters = defaultFilters;

    if (typeof queryFilters === "string") {
      try {
        _filters = {
          ...defaultFilters,
          ...validationSchema.parse(JSON.parse(queryFilters)),
        };
      } catch {}
    }

    return _filters;
  }, [defaultFilters, validationSchema, queryFilters]);

  const setFilters = useCallback(
    (partialFilters: Partial<T>) => {
      let currentFilters = defaultFilters;
      const currentFiltersSearch = new URLSearchParams(
        window.location.search,
      ).get(URL_FILTERS_SEARCH_KEY);

      if (currentFiltersSearch) {
        try {
          currentFilters = validationSchema.parse(
            JSON.parse(currentFiltersSearch),
          );
        } catch {}
      }

      const newFilters: T = {
        ...currentFilters,
        ...partialFilters,
      };

      if (isEqual(currentFilters, newFilters)) return;

      const newQuery: ParsedUrlQueryInput = {
        page: 1,
      };

      if (!isEqual(newFilters, defaultFilters)) {
        newQuery.filters = JSON.stringify(newFilters);
      }

      startTransition(() => {
        Router.replace({ query: newQuery }, undefined, { shallow: true });
      });
    },
    [defaultFilters, validationSchema],
  );

  return [filters, setFilters] as const;
}
